import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Settings() {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    registration_number: '',
    vat_number: '',
    tax_number: '',
    industry: '',
    address: '',
    contact_info: ''
  });

  useEffect(() => {
    loadCompany();
  }, []);

  const loadCompany = async () => {
    try {
      const { data } = await axios.get(`${API}/company`, { withCredentials: true });
      setCompany(data);
      setFormData({
        name: data.name || '',
        registration_number: data.registration_number || '',
        vat_number: data.vat_number || '',
        tax_number: data.tax_number || '',
        industry: data.industry || '',
        address: data.address || '',
        contact_info: data.contact_info || ''
      });
    } catch (error) {
      toast.error('Failed to load company data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/company`, formData, { withCredentials: true });
      toast.success('Company profile updated');
      loadCompany();
    } catch (error) {
      toast.error('Failed to update company profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}} data-testid="settings-title">
          Settings
        </h1>
        <p className="mt-2 text-zinc-600">Manage your company profile</p>
      </div>

      <div className="bg-white border border-zinc-200 max-w-3xl">
        <div className="p-6 border-b border-zinc-200">
          <h2 className="text-xl font-semibold" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Company Profile</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Company Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                data-testid="company-name-input"
                className="mt-2 border-zinc-300 hover:border-black transition-colors"
              />
            </div>
            <div>
              <Label htmlFor="registration_number" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Registration Number</Label>
              <Input
                id="registration_number"
                value={formData.registration_number}
                onChange={(e) => setFormData({...formData, registration_number: e.target.value})}
                data-testid="registration-number-input"
                className="mt-2 border-zinc-300 hover:border-black transition-colors"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label htmlFor="vat_number" className="text-xs font-bold uppercase tracking-wider text-zinc-600">VAT Number</Label>
              <Input
                id="vat_number"
                value={formData.vat_number}
                onChange={(e) => setFormData({...formData, vat_number: e.target.value})}
                data-testid="vat-number-input"
                className="mt-2 border-zinc-300 hover:border-black transition-colors"
              />
            </div>
            <div>
              <Label htmlFor="tax_number" className="text-xs font-bold uppercase tracking-wider text-zinc-600">SARS Tax Number</Label>
              <Input
                id="tax_number"
                value={formData.tax_number}
                onChange={(e) => setFormData({...formData, tax_number: e.target.value})}
                data-testid="tax-number-input"
                className="mt-2 border-zinc-300 hover:border-black transition-colors"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="industry" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Industry</Label>
            <Input
              id="industry"
              value={formData.industry}
              onChange={(e) => setFormData({...formData, industry: e.target.value})}
              data-testid="industry-input"
              className="mt-2 border-zinc-300 hover:border-black transition-colors"
            />
          </div>

          <div>
            <Label htmlFor="address" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Address</Label>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              data-testid="address-input"
              className="mt-2 border-zinc-300 hover:border-black transition-colors"
            />
          </div>

          <div>
            <Label htmlFor="contact_info" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Contact Information</Label>
            <Input
              id="contact_info"
              value={formData.contact_info}
              onChange={(e) => setFormData({...formData, contact_info: e.target.value})}
              data-testid="contact-info-input"
              className="mt-2 border-zinc-300 hover:border-black transition-colors"
            />
          </div>

          <Button
            type="submit"
            disabled={saving}
            data-testid="save-settings-button"
            className="bg-black text-white hover:bg-zinc-800 h-11 font-semibold transition-colors"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </form>
      </div>
    </div>
  );
}
